import { ComponentPropsWithoutRef, useMemo } from 'react';

import { ProgressBar, RatingItems, useModalController } from '@uikit';

import { useInfiniteReviewsQuery } from '@query';

import { useAuth } from '@shared/common/providers/AuthProvider';
import {
  useAuthModal,
  useCompleteMagicIdModal,
} from '@shared/common/providers/GlobalModalsProvider';
import { transformReview } from '@shared/helpers/review';

import ReviewsSlider from '@store:web/components/common/ReviewsSlider/ReviewsSlider';

import AddUserReviewModal from './AddUserReviewModal';
import { AppPageSectionProps } from './AppPage';
import ReviewListModal from './ReviewListModal';

type SvgEyeProps = ComponentPropsWithoutRef<'svg'>;

export const SvgEye = (props: SvgEyeProps) => {
  return (
    <svg {...props}>
      <ellipse
        cx="49.072"
        cy="52.579"
        rx="40.987"
        ry="43.914"
        fill="#fff"
        stroke="#0D052C"
        strokeWidth="1.749"
        strokeLinecap="round"
        strokeLinejoin="round"
      />
      <ellipse
        cx="49.072"
        cy="51.688"
        rx="26.695"
        ry="28.602"
        fill="#FF5986"
        stroke="#0D052C"
        strokeWidth="1.749"
        strokeLinecap="round"
        strokeLinejoin="round"
      />
      <ellipse
        cx="49.17"
        cy="52.578"
        rx="11.904"
        ry="12.754"
        fill="#1D1E5F"
        stroke="#0D052C"
        strokeWidth="1.749"
        strokeLinecap="round"
        strokeLinejoin="round"
      />
      <path
        d="M59.268 16.602a33.274 33.274 0 0 1 8.976 4.623M43.911 27.023l3.002 12.8M44.045 78.128l3.002-12.8M35.63 31.016l6.612 11.137M28.907 38.32l10.029 7.662M25.52 48.281l11.962 1.88M25.872 58.274l11.61-3.223M29.276 67.53l9.66-8.19M36.022 74.204l6.562-11.169M54.413 27.023l-3.002 12.8M54.208 77.82l-2.93-12.492M62.694 31.016l-6.612 11.137M69.417 38.32 59.39 45.982M72.805 48.281l-11.73 1.845M72.452 58.274l-11.603-3.223M69.048 67.53l-9.66-8.19M62.459 74.47l-6.72-11.435"
        stroke="#0D052C"
        strokeWidth="1.749"
        strokeLinecap="round"
        strokeLinejoin="round"
      />
      <ellipse
        cx="57.301"
        cy="46.111"
        rx="5.89"
        ry="6.311"
        fill="#fff"
        stroke="#0D052C"
        strokeWidth="1.749"
        strokeLinecap="round"
        strokeLinejoin="round"
      />
    </svg>
  );
};

const AppUserScoreSection = ({ app }: AppPageSectionProps) => {
  const {
    attributes: { rating, ratings },
  } = app;
  const { isAuth, user } = useAuth();
  const {
    data,
    isLoading: isReviewListLoading,
    hasNextPage,
    fetchNextPage,
    isFetchingNextPage,
  } = useInfiniteReviewsQuery({
    appId: app.attributes.appLink,
  });
  const reviewModal = useModalController();
  const completeMagicIdModal = useCompleteMagicIdModal();
  const reviews = useMemo(
    () => data?.pages.flatMap((page) => page.map((review) => transformReview({ review }))) || [],
    [data],
  );
  const sliderReviews = reviews.slice(0, 8);
  const authModal = useAuthModal();

  const handleAddUserReviewButtonClick = () => {
    if (!isAuth) {
      authModal.open();

      return;
    }

    if (!user.attributes.name) {
      completeMagicIdModal.open({
        description: {
          name: null,
        },
        success: false,
      });

      return;
    }

    reviewModal.open();
  };

  return (
    <section className="sm:app-page-section-card flex flex-col" data-qa="review-block">
      <header className="flex justify-between mb-8">
        <h2 className="t-section-title" data-qa="app-user-score-title">
          {app.attributes.appName} Review Score by Real{' '}
          {app.attributes.category?.toLowerCase().startsWith('game') ? 'Gamers' : 'Users'}
        </h2>

        {!!reviews?.length && (
          <ReviewListModal
            app={app}
            title="Reviews"
            isLoading={isReviewListLoading}
            hasMore={hasNextPage}
            onLoadMore={fetchNextPage}
            isLoadingMore={isFetchingNextPage}
            showReviewFlag
            reviews={reviews}
          />
        )}
      </header>

      <div className="flex flex-col gap-6">
        <div className="flex max-sm:flex-col max-sm:justify-center gap-6">
          <div className="flex items-center lg:border-b lg:border-neutral-100 lg:pb-5 lg:items-end lg:flex-1">
            <span className="flex w-full items-baseline justify-between">
              <span className="items-center inline-flex max-lg:flex-col lg:items-baseline font-medium gap-1">
                <span className="t-h1" data-qa="review-score">
                  {Number(rating) || 0}
                </span>
                Out of 5
              </span>

              <span className="max-lg:hidden t-caption1 font-medium" data-qa="total-rating">
                {ratings.all} Total
              </span>
            </span>
          </div>

          <ul className="flex-1 flex flex-col gap-2">
            {Array.from({ length: 5 }, (_, i) => {
              const numberOfStars = (5 - i) as 1 | 2 | 3 | 4 | 5;
              const totalReviews = ratings[numberOfStars];
              const progressBar = (totalReviews * 100) / ratings.all;

              return (
                <li key={i} className="flex items-center">
                  <RatingItems
                    className="flex-shrink-0 justify-end mr-5 w-[6.25rem]"
                    score={5 - i}
                    shouldRenderInactiveItems={false}
                  />

                  <ProgressBar
                    indicatorAriaLabel={`The number of users who gave a rating of ${numberOfStars}`}
                    className="flex-1 h-1 bg-neutral-100 text-neutral-700"
                    value={progressBar}
                  />

                  <span
                    className="w-10 text-sm text-neutral-500 flex-shrink-0 text-right"
                    data-qa="review-rating"
                  >
                    {totalReviews}
                  </span>
                </li>
              );
            })}
          </ul>
        </div>
        <div className="flex justify-between items-center mb-7">
          <button
            onClick={handleAddUserReviewButtonClick}
            className="button button-solid button-lg flex-grow-0 self-start mt-auto"
          >
            + Add User Review
          </button>
          <AddUserReviewModal app={app} {...reviewModal.props} />

          <span className="lg:hidden t-caption1 font-medium">{ratings.all} Total</span>
        </div>
      </div>

      <div className="min-h-[20rem]">
        {!!sliderReviews?.length && (
          <ReviewsSlider
            showReviewFlag
            showReviewFlagInModal
            app={app.attributes}
            id="user-reviews-slider"
            reviews={sliderReviews}
            className="-ml-store-container-px sm:-ml-app-page-section-card-p"
            sliderClassName="pl-store-container-px sm:pl-app-page-section-card-p"
          />
        )}

        {!sliderReviews?.length && (
          <div className="h-full flex flex-col justify-end" data-qa="empty-reviews">
            <p className="text-center my-auto font-medium text-sm">
              Be the first to post the User Review
            </p>

            <div
              className="relative w-full rounded-full bg-primary-200 h-20"
              data-qa="empty-reviews-art"
            >
              <SvgEye className="w-24 h-auto absolute left-0 right-0 top-0 m-auto -translate-y-1/3" />
            </div>
          </div>
        )}
      </div>
    </section>
  );
};

export default AppUserScoreSection;
